/* eslint-disable */

/**
 * trigger pipe
 */

import '../css/app.css';
import './webpack.tweaks';
import './vendor';
import { migrateAndDeleteLegacyIDB } from '@/apps/order/classes/AppIDB';
import Vue, { provide } from 'vue';
import { OrdersCreateRouter } from './apps/order/routes';
import { createPinia, PiniaVuePlugin } from 'pinia';

import './modules/layouts';

import './modules/account';
import './modules/api';
import './modules/base';
import './modules/center';
import './modules/shop';
import './modules/products';
import './modules/order';
import './modules/sales';

import './modules/libs';
import './modules/base-components';
import './modules/components';

import Equalize from './utils/Equalize';
import EqualizeTables from './utils/EqualizeTables';
import IoForm from './components/forms/IoForm.vue';
import IoSectionTop from './components/IoSectionTop.vue';
import IoDataTable from './components/IoDataTable.vue';
import IoMobileNavigation from './components/menu/IoMobileNavigation.vue';
import IoHeader from './components/menu/IoHeader.vue';
import IoSidebar from './components/menu/IoSidebar.vue';
import IoMessageBanner from './components/IoMessageBanner.vue';
import store from './store';
import { Popover } from 'bootstrap';

import RouterStoreInterface from '@/interfaces/RouterStoreInterface';
import { TextHelper, $text } from '@/utils/TextHelper';
import iconAccessibility from '@/utils/IconAccessibility';
import { fireAlert } from '@/apps/center/classes/MeterReadingFormSubmissionHelper';
import { ioTrack } from '@/utils/ioTrack';

// Global mixin to make text helper available in all components
Vue.mixin({
  methods: {
    $text: (category, key, defaultText = '') => $text(category, key, defaultText),
  },
});

// Make text helper available globally
if (typeof window !== 'undefined') {
  window.$text = $text;
}

declare global {
  interface Window {
    IOC: Record<string, any>
    API_URLS: Record<string, any>
    ROUTER: RouterStoreInterface
    GlobalEvents: {
      fire: (event: string, payload: any) => void,
      listen: (event: string, callback: (data?: any) => void) => void,
      destroy: (event: string) => void,
    },
    texts: Record<string, any>
    $text: (category: string, key: string, defaultText?: string) => string,
    lastSync: string | null,
    syncUrl: string
    app?: string
    customerId?: string
    alertsInitialized?: boolean
  }

  interface Date {
    format(format: string): string
    addDays(days: number): Date
  }
}
Vue.prototype.$_testing = true;

Vue.use(PiniaVuePlugin);

const pinia = createPinia();

window.IOC.app = new Vue({
  router: OrdersCreateRouter,
  el: '#ioapp',
  store,
  pinia,
  setup() {
    provide('vuex-store', store);
    provide('texts', window.texts);

    // Initialize text helper
    TextHelper.init();

    // Initialize icon accessibility utility
    iconAccessibility.init();
  },

  components: {
    IoForm,
    IoSectionTop,
    IoDataTable,
    IoMobileNavigation,
    IoHeader,
    IoSidebar,
    IoMessageBanner,
  },
  data() {
    return {
      cssVariables: [],
      equialize: new Equalize(),
      equializeTables: new EqualizeTables(),
      GlobalEvents: window.GlobalEvents,
    };
  },

  mounted() {
    // Make text helper available globally
    window.$text = $text;

    // Process icons after Vue has mounted
    iconAccessibility.process();

    const popovers = [].slice
      .call(document.querySelectorAll('[data-bs-toggle="popover"]'))
      /* eslint-disable-next-line */
      .map((popoverTriggerEl) => new Popover(popoverTriggerEl, {
        html: true,
        content() {
          const popoverContentEl = document.querySelector(this.dataset.bsContentTarget || 'div');

          if (popoverContentEl !== null) {
            return popoverContentEl.innerHTML.trim();
          }

          return '';
        },
      }));

    this.initIoConfirmLinks();

    this.equialize.setEventHandler();
    this.equialize.run();
    this.equializeTables.setEventHandler();
    this.equializeTables.run();

    if (window.app === 'center' && window.customerId !== null) {
      this.$nextTick(() => {
        localStorage.setItem('lastSyncCustomer-' + window.customerId, (new Date(Number(window.lastSync))).getTime()
          .toString());

        this.checkLastSync();
      });

      setInterval(() => { // check for every 5 minutes if the last sync date not today
        this.checkLastSync();
      }, 1000 * 60 * 5);
    }
  },

  methods: {
    initIoConfirmLinks() {
      document
        .querySelectorAll('.io-confirm-link:not(.mounted)')
        .forEach((element: Element) => {
          const HTMLElement = element as unknown as HTMLElement;

          HTMLElement.addEventListener('click', (e) => {
            e.preventDefault();

            // @ts-ignore
            this.$confirm({
              message: HTMLElement.dataset.text,
              button: {
                no: HTMLElement.dataset.textPrevent ?? 'Abrechen',
                yes: HTMLElement.dataset.textConfirm ?? 'Fortfahren',
              },

              callback: (confirm: boolean) => {
                if (confirm) {
                  window.location.href = HTMLElement.getAttribute('href') || '#';
                }
              },
            });
          });

          element.classList.add('mounted');
        });
    },
    checkLastSync() {
      let lastSync = Number(window.lastSync);
      if (localStorage.getItem('lastSyncCustomer-' + window.customerId) !== null) {
        lastSync = Number(localStorage.getItem('lastSyncCustomer-' + window.customerId));
      }

      if ((new Date()).getDate() - (new Date(lastSync)).getDate() !== 0) {
        fetch(window.syncUrl)
          .then((response) => {
            if (response.status === 400) {
              return;
            }

            localStorage.setItem('lastSyncCustomer-' + window.customerId, (new Date()).getTime()
              .toString());

            const showNotification = () => {
              fireAlert('info', 'Die Synchronisierung wurde erfolgreich gestartet. Sie werden in wenigen Sekunden automatisch auf unsere Homepage weitergeleitet.');
              setTimeout(() => {
                window.location.reload();
              }, 5000);
            };

            if (window.alertsInitialized) {
              showNotification();
            } else {
              let attempts = 0;
              const maxAttempts = 50;
              const waitForAlerts = setInterval(() => {
                attempts++;
                if (window.alertsInitialized) {
                  clearInterval(waitForAlerts);
                  showNotification();
                } else if (attempts >= maxAttempts) {
                  clearInterval(waitForAlerts);
                  console.error('Alerts could not be initialized.');
                }
              }, 200);
            }
          })
          .catch(e => {
            console.error('Error checking last sync:', e);
          });
      }
    }
  },
});

// Run now, not on 'load': with the async module boot, 'load' may have fired already.
migrateAndDeleteLegacyIDB();

// Tells other modules' blades that window.Vue etc. are set; late ones await window.ioAppReady.
(window as any).ioAppReady = Promise.resolve();
window.dispatchEvent(new Event('io:app-ready'));

// Fired here (not in CenterPwaStore) so it also catches an install via the browser's own menu,
// which isn't tied to the store ever being instantiated, and so changes to ioTrack.ts don't
// pull in every test that depends on the store.
window.addEventListener('appinstalled', () => {
  ioTrack('portal.app', 'pwa_installed');
});
